import type { ChangeEventHandler, RefObject } from "react";
import React, { useEffect } from "react";
import type {
  DeepMap,
  FieldElement,
  FieldError,
  FormState,
  Ref,
  RegisterOptions,
} from "react-hook-form";
import styled from "styled-components/macro";
import type { FontType } from "../../types/types";
import { showErrors } from "../../util/util-components";
import { useTranslation } from "react-i18next";

const Input = styled.input.attrs({})<{
  invalid?: boolean;
  fontSize?: FontType;
  fontWeight?: FontType;
}>`
  height: 32px;
  width: fit-content;
  min-width: 100px;
  border: ${({ theme, invalid }) =>
    invalid
      ? `${theme.resolve("border_md")} solid ${theme.errorColor}`
      : `${theme.resolve("border_md")} solid ${theme.tertiaryBorder}`};
  padding: ${({ theme }) => theme.resolve("spacing_xs")};
  font-size: ${({ theme, fontSize }) => theme.fontSizes[fontSize ?? "large"]};
  font-weight: ${({ theme, fontWeight }) =>
    theme.fontWeights[fontWeight ?? "large"]};
  outline: none;
  margin: 0;
  &:focus {
    border-radius: ${({ theme }) => theme.resolve("radius_xs")};
    border: ${({ theme, invalid }) =>
      invalid
        ? `${theme.resolve("border_2xl")} solid ${theme.errorColor}`
        : `${theme.resolve("border_2xl")} solid ${theme.tertiaryBorder}`};
  }
`;

export const EditModeTitle = ({
  theref,
  register,
  name,
  defaultValue,
  tabIndex,
  formState,
  errors,
  onChange,
  fontSize,
  fontWeight,
  stopClickPropagation,
}: {
  theref?: RefObject<HTMLInputElement>;
  register: <TFieldElement extends FieldElement<any>>(
    ref: (TFieldElement & Ref) | null,
    rules?: RegisterOptions
  ) => void;
  name: string;
  defaultValue: string;
  tabIndex?: number;
  formState: FormState<Record<string, any>>;
  errors: DeepMap<Record<string, any>, FieldError>;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  fontSize?: FontType;
  fontWeight?: FontType;
  /**
   * Set when the title is nested inside another clickable element (e.g. an
   * AccordionCard header that toggles on click) so clicking into the input
   * doesn't also trigger that ancestor's click handler. Defaults to off so
   * existing callers keep their current bubbling behavior.
   */
  stopClickPropagation?: boolean;
}) => {
  const { t } = useTranslation();
  const onClick = stopClickPropagation
    ? (e: React.MouseEvent<HTMLInputElement>) => e.stopPropagation()
    : undefined;

  useEffect(() => {
    if (theref) {
      register(theref.current);
      theref.current?.focus();
    }
  }, [register, theref]);
  if (
    (formState.submitCount > 0 && errors[name]?.message) ||
    errors[name]?.type
  ) {
    return (
      <>
        <Input
          autoComplete="on"
          ref={theref}
          type="text"
          name={name}
          defaultValue={defaultValue}
          onChange={onChange}
          invalid
          tabIndex={tabIndex}
          fontSize={fontSize}
          fontWeight={fontWeight}
          onClick={onClick}
        />
        {showErrors({ errors, name, t })}
      </>
    );
  }
  return (
    <Input
      type="text"
      ref={theref}
      name={name}
      required
      defaultValue={defaultValue}
      onChange={onChange}
      tabIndex={tabIndex}
      fontSize={fontSize}
      fontWeight={fontWeight}
      onClick={onClick}
    />
  );
};
